import { z } from 'zod';

const timeString = z.string().optional().or(z.literal(''));

const breakSchema = z.object({
  from: timeString,
  to: timeString
});

const workingDaySchema = z.object({
  open: z.boolean().optional(),
  from_time: timeString,
  to_time: timeString,
  breaks: z.array(breakSchema).optional()
});

const workingHoursSchema = z.object({
  monday: workingDaySchema,
  tuesday: workingDaySchema,
  wednesday: workingDaySchema,
  thursday: workingDaySchema,
  friday: workingDaySchema,
  saturday: workingDaySchema,
  sunday: workingDaySchema
});

const holidaySchema = z.object({
  date: z.string().min(1, 'Date is required'),
  reason: z.string().optional().or(z.literal(''))
});

const hasPhone = (phone?: string | null) => Boolean(String(phone ?? '').replace(/[^\d]/g, '').length > 4);
const hasText = (value?: string | number | null) => Boolean(String(value ?? '').trim());

export const teamMemberSchema = z.object({
  team_member: z
    .object({
      first_name: z.string().min(1, 'Name is required'),
      email: z.string().email('Please enter a valid email address').optional().nullish().or(z.literal('')),
      phone_number: z.string().optional().nullish().or(z.literal('')),
      sms_consent: z.boolean().optional(),
      sip_uri: z.any().optional().nullish(),
      extension: z.any().optional().nullish(),
      main_line_number: z.any().optional().nullish(),
      role: z.enum(['business_admin', 'location_admin', 'operator'], {
        message: 'Role is required'
      }),
      location_ids: z.any().optional().nullable(),
      working_hours: workingHoursSchema.optional(),
      holidays: z.array(holidaySchema).optional()
    })
    .superRefine((data, ctx) => {
      const emailPresent = hasText(data.email);
      const phonePresent = hasPhone(data.phone_number);
      const extensionPresent = hasText(data.extension);
      const sipPresent = hasText(data.sip_uri);
      const hasContactMethod = phonePresent || extensionPresent || sipPresent;

      if (data.role === 'location_admin') {
        const selectedIds = Array.isArray(data.location_ids)
          ? data.location_ids.filter((id) => id !== '' && id != null)
          : data.location_ids !== '' && data.location_ids != null
            ? [data.location_ids]
            : [];
        if (selectedIds.length === 0) {
          ctx.addIssue({
            path: ['location_ids'],
            code: 'custom',
            message: 'Please select at least one location'
          });
        }
      }

      if (data.role === 'operator') {
        // Operator needs phone, extension, or SIP; otherwise email is required
        if (!hasContactMethod && !emailPresent) {
          ctx.addIssue({
            path: ['email'],
            code: 'custom',
            message: 'Provide a phone number, extension, SIP URI, or email'
          });
        }
        return;
      }

      // Platform roles (business/location admin) require email
      if (!emailPresent) {
        ctx.addIssue({
          path: ['email'],
          code: 'custom',
          message: 'Email is required'
        });
      }
    })
});

export const agencyTeamMemberSchema = z.object({
  team_member: z.object({
    name: z.string({ message: 'Email is required' }),
    email: z.string({ message: 'Email is required' }).email({ message: 'Please enter a valid email address' }),
    phone_number: z.any().optional().nullable(),
    role: z.enum(['agency_admin', 'agency_admin_readonly', 'agent_builder'], {
      message: 'Role is required'
    })
  })
});
